/**
 * Propositions d'un QCM V2 : la bonne réponse (`answer_md` du checkpoint) et
 * ses distracteurs, mélangés avec une graine stable par tentative. Partagé par
 * l'affichage en cartes (`QcmChoices`) et l'affichage en touches (feuille).
 */

import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';
import { childrenOf, correctChoiceIds, visualsOf } from '@/lib/exercise-v2/block-helpers';

export interface QcmOption {
  id: string;
  label: string;
  visuals: ExerciseVisualBlock[];
  is_correct: boolean;
}

/** Mélange Fisher-Yates à graine (stable pour une tentative). */
export function seededShuffle<T>(arr: T[], seed: number): T[] {
  const out = [...arr];
  let s = seed;
  const rnd = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function buildQcmOptions(
  checkpoint: ExerciseBlock,
  blocks: ExerciseBlock[],
  seed: number,
): QcmOption[] {
  const correctSet = new Set(correctChoiceIds(blocks, checkpoint));
  const corr: QcmOption = {
    id: checkpoint.id,
    label: String((checkpoint.data as { answer_md?: string }).answer_md ?? '?'),
    visuals: visualsOf(checkpoint),
    is_correct: true,
  };
  const wrong = childrenOf(blocks, checkpoint.id)
    .filter((b) => b.block_type === 'distractor')
    .map(
      (d): QcmOption => ({
        id: d.id,
        label: String((d.data as { label_md?: string }).label_md ?? '?'),
        visuals: visualsOf(d),
        // en multiple, un distracteur marqué is_correct est une bonne réponse
        is_correct: correctSet.has(d.id),
      }),
    );
  return seededShuffle([corr, ...wrong], seed);
}
